/**
 * Debounced search-box state — the one implementation of the
 * "draft locally, commit the settled value" pattern (extracted from the
 * Queue tab; every search box that writes a URL param or triggers a
 * fetch should sit behind it).
 *
 * The returned `draft` is the live input value: bind it to the
 * TextInput and, where filtering is client-side, filter on it directly
 * so results are keystroke-instant. `onCommit` fires once per settled
 * (300ms-idle) value with the TRIMMED draft — that's the value to put
 * in the URL / send to the server. Commits never fire for echoes of
 * the committed value, and an external change to `committed`
 * (back/forward navigation, another tab writing the shared param)
 * resets the draft — but never while it would clobber in-flight
 * typing.
 */

import { useEffect, useRef, useState } from 'react';

import { useDebouncedValue } from '@mantine/hooks';

export function useSearchDraft(
  committed: string,
  onCommit: (settled: string) => void,
  delayMs = 300
): [string, (next: string) => void] {
  const [draft, setDraft] = useState(committed);
  const [debounced] = useDebouncedValue(draft, delayMs);
  // Latest-commit ref so the effect never re-fires on callback identity
  // churn (the page recreates handlers on every URL change).
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const committedRef = useRef(committed);
  committedRef.current = committed;

  useEffect(() => {
    const settled = debounced.trim();
    if (settled !== committedRef.current) {
      onCommitRef.current(settled);
    }
    // Deliberately keyed on the settled draft alone — `committed`
    // updating to echo our own commit must not re-fire this.
  }, [debounced]);

  useEffect(() => {
    // Reset only when the change can't be an echo of our own commit
    // (committed === trimmed debounced) or of what's being typed right
    // now (committed === trimmed draft).
    if (committed !== debounced.trim() && committed !== draft.trim()) {
      setDraft(committed);
    }
    // Keyed on the committed value alone: draft/debounced are compared
    // at reset-time, not watched.
  }, [committed]);

  return [draft, setDraft];
}
